// Who's in each family column of the People table (parents, partners, siblings, children,
// friends), and adding or removing someone there. Adding makes the plainest link of that kind;
// the relationship editor refines it.

import { childrenOf, graphOf, other, parentsOf, partnersOf, siblingsOf } from './graph';
import { addRelationship, findRelationship, removeRelationship, wouldCreateCycle } from './tree';
import type { Id, Relationship, SiblingType, SocialType, Tree } from './types';

export type KinColumn = 'parents' | 'partners' | 'siblings' | 'children' | 'friends';

/** Social links that count as friends. */
export const FRIEND_TYPES = new Set<SocialType>(['friend', 'close-friend', 'best-friend']);

export interface Relative {
  id: Id;
  /** The links that put them in this column (none for siblings only through shared parents). */
  rels: Relationship[];
  /** Siblings through shared parents stay siblings until a parent link changes. */
  throughParents?: Id[];
  /** For siblings: full, half, step, and so on, as worked out from the tree. */
  siblingType?: SiblingType;
}

export function relativesOf(t: Tree, id: Id, col: KinColumn): Relative[] {
  const g = graphOf(t);
  switch (col) {
    case 'parents':
      return parentsOf(g, id).map((p) => ({ id: p, rels: (g.parentRels.get(id) ?? []).filter((r) => r.a === p) }));
    case 'children':
      return childrenOf(g, id).map((c) => ({ id: c, rels: (g.childRels.get(id) ?? []).filter((r) => r.b === c) }));
    case 'partners':
      return partnersOf(g, id).map((p) => ({ id: p, rels: (g.partnerRels.get(id) ?? []).filter((r) => other(r, id) === p) }));
    case 'siblings':
      return siblingsOf(g, id).map((s) => ({ id: s.id, rels: s.explicit ? [s.explicit] : [], throughParents: s.sharedParents.length ? s.sharedParents : undefined, siblingType: s.type }));
    case 'friends': {
      const out = new Map<Id, Relative>();
      for (const r of g.socialRels.get(id) ?? []) {
        if (!FRIEND_TYPES.has(r.type)) continue;
        const o = other(r, id);
        const entry = out.get(o) ?? { id: o, rels: [] };
        entry.rels.push(r);
        out.set(o, entry);
      }
      return [...out.values()];
    }
  }
}

/** Why someone couldn't be added: they're the same person, already there, linked another way, or it would loop the tree. */
export type AddProblem = 'self' | 'exists' | 'connected' | 'cycle';

/** Link `otherId` to `id` in a column: a biological parent or child, a current partner, a sibling, or a friend. */
export function addRelative(t: Tree, id: Id, col: KinColumn, otherId: Id): { tree: Tree } | { problem: AddProblem } {
  if (id === otherId) return { problem: 'self' };
  if (relativesOf(t, id, col).some((r) => r.id === otherId)) return { problem: 'exists' };
  switch (col) {
    case 'parents':
      if (wouldCreateCycle(t, otherId, id)) return { problem: 'cycle' };
      return { tree: addRelationship(t, { kind: 'parent', a: otherId, b: id, type: 'biological' }).tree };
    case 'children':
      if (wouldCreateCycle(t, id, otherId)) return { problem: 'cycle' };
      return { tree: addRelationship(t, { kind: 'parent', a: id, b: otherId, type: 'biological' }).tree };
    case 'partners':
      return { tree: addRelationship(t, { kind: 'partner', a: id, b: otherId, type: 'partner', status: 'current' }).tree };
    case 'siblings':
      return { tree: addRelationship(t, { kind: 'sibling', a: id, b: otherId, type: 'unknown' }).tree };
    case 'friends':
      // One social link per pair: a colleague or mentor is changed in the relationship editor instead.
      if (findRelationship(t, 'social', id, otherId)) return { problem: 'connected' };
      return { tree: addRelationship(t, { kind: 'social', a: id, b: otherId, type: 'friend' }).tree };
  }
}

/** Remove the links that put `otherId` in this column. Siblings through shared parents can't be removed this way. */
export function removeRelative(t: Tree, id: Id, col: KinColumn, otherId: Id): Tree {
  const rel = relativesOf(t, id, col).find((r) => r.id === otherId);
  if (!rel || rel.throughParents) return t;
  return rel.rels.reduce((acc, r) => removeRelationship(acc, r.id), t);
}
